<script lang="ts">
	import {
		ALL,
		getCartIconContext,
	} from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { DropPageGenerator } from '#lib/features/drop-page/components/DropPageGenerator/index.js';
	import { DropPageViewAfterOpenTracker } from '#lib/features/drop-page/components/DropPageViewAfterOpenTracker/index.js';
	import type { DropSections } from '#lib/features/drop-page/constants/dropSectionImports/types.js';
	import type { DropPageItem } from '#lib/features/drop-page/constants/types.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { getLiveSlug } from '#lib/features/live-drop-info/utils/getLiveSlug/getLiveSlug.js';
	import { PrimaryMixpanelEvents } from '#lib/websites/primary/constants/mixpanel-events.js';
	import { page } from '$app/state';
	import { buildContentKey } from '$global/utils/contentKey';
	import type { PrimaryReleasePDPPageData } from '$routes/primary/release/[slug]/types';

	const { dropTypes } = getCartIconContext();

	const { liveDropInfoCtx } = getLiveDropInfoContext();

	let showRegisterInterest = $derived(
		page.data
			.showRegisterInterest as PrimaryReleasePDPPageData['showRegisterInterest']
	);
	// Both loads parse the items' Markdown (`parseDropComponentsMarkdownServer`),
	// the REST one after its representation-only cast to the GraphQL item union
	// (rest-migration/feature-flags.md rule 4).
	let componentItems = $derived(page.data.componentItems as DropPageItem[]);
	// The components those items render, imported by the route's universal
	// load (`loadDropSections`).
	let dropSections = $derived(page.data.dropSections as DropSections);
	let primaryCollection = $derived(
		page.data
			.primaryCollection as PrimaryReleasePDPPageData['primaryCollection']
	);
	let pastDropType = $derived(
		page.data.pastDropType as PrimaryReleasePDPPageData['pastDropType']
	);

	let showRegisterCode = $derived(
		(() => {
			// If the show_register_interest field is disabled for this drop page
			// Then the component should not be shown
			if (!showRegisterInterest) {
				return null;
			}

			// If the drops active open for application are not loaded yet
			// Or if the drop is not open for applications
			// Then we can show the show_register_interest component
			if (
				pastDropType ||
				(!!$dropTypes &&
					!liveDropInfoCtx.liveDropInfo?.liveSlugs.includes(
						getLiveSlug(primaryCollection?.drop_page?.slug as string, 'primary')
					))
			) {
				return primaryCollection?.code;
			}

			// Otherwise, we don't show it
			return null;
		})()
	);

	let showCardCartButton = $derived(
		!!liveDropInfoCtx.liveDropInfo?.liveSlugs?.includes(
			getLiveSlug(primaryCollection?.drop_page?.slug as string, 'primary')
		) && !pastDropType
	);

	// Partial content key (HB-974): a release covers several artworks, so there is
	// no single price or medium. The drop config is the thing being identified —
	// the page is about the drop — which also carries the drop attribution the
	// register-interest click needs (HB-970).
	let registerInterestContentKey = $derived(
		buildContentKey({
			artist: primaryCollection?.artists
				?.map((artist) => artist?.Artists_id?.name)
				.filter(Boolean)
				.join(', '),
			idValue: page.data.dropConfigId,
			idCollection: 'Drop_Config',
			release: primaryCollection?.name,
			category: primaryCollection?.name,
			platform: 'primary',
		})
	);
</script>

<DropPageViewAfterOpenTracker
	event={PrimaryMixpanelEvents.DropPageViewAfterOpen}
	dropConfigId={page.data.dropConfigId}
	slug={primaryCollection?.drop_page?.slug || ''}
	dropStartDate={page.data.dropStartDate}
	isLive={showCardCartButton}
/>

<DropPageGenerator
	dropConfigId={page.data.dropConfigId}
	productId={ALL}
	{showRegisterCode}
	{registerInterestContentKey}
	{componentItems}
	sections={dropSections}
	showCardButtons
	{showCardCartButton}
	{pastDropType}
/>
